<script>
export default {
	name: 'DestinationsListingSearch',
	inheritAttrs: false,
};
</script>

<script setup>
import CTAButton from '~/aem-components/CTAButton.vue';
import { debounce as _debounce } from 'lodash';
import { ensureHtmlExtension, wrapQueryWithHTML } from '~/logic/helpers/string.js';
import { onClickOutside } from '@vueuse/core';
import { useIsRTL } from '~/logic/helpers/is-rtl.js';

const props = defineProps({
	searchInputPlaceholderText: { type: String, default: '' },
	searchButtonLabelText: { type: String, default: '' },
	searchList: { type: Array, default: null },
	regionFilter: { type: String, default: '' },
	defaultValue: { type: String, default: '' },
	componentIdAA: { type: String, default: '' },
});
const emit = defineEmits([
	'update:searchQuery',
]);

const searchInputVal = ref(props.defaultValue);
const searchSuggestion = ref([]);
const searchSuggestionByOrder = ref([]);
const showSuggestion = ref(false);
const rootEl = ref(null);

const getSearchSuggestion = async (query) => {
	if (props.regionFilter == 'all') {
		searchSuggestion.value = props.searchList.filter((item) => {
			return item.title.toLowerCase().includes(query.toLowerCase());
		});
	} else {
		searchSuggestion.value = props.searchList.filter((item) => {
			let isShowTile = false;
			if (item.pageProperties?.destinationRegionTag.length > 0) {
				item.pageProperties?.destinationRegionTag.forEach((tag) => {
					if (tag.value == props.regionFilter) {
						if (item.title.toLowerCase().includes(query.toLowerCase())) {
							isShowTile = true;
						}
					}
				});
			}
			return isShowTile;
		});
	}
	searchSuggestion.value = searchSuggestion.value.map((item) => ({
		...item,
		'position': item.title.toLowerCase().indexOf(query.toLowerCase()),
	}));

	searchSuggestionByOrder.value = searchSuggestion.value.sort((a, b) => a.position - b.position);
	if (!query) {
		emit('update:searchQuery', '');
	}
	if (searchSuggestionByOrder.value.length > 0) {
		showSuggestion.value = true;
	}
};

const handleOnFocus = async (event) => {
	await getSearchSuggestion(event.target.value);
};
const handleOnInput = async (event) => {
	if (event.target.value.length >= 3
		|| event.target.value.length == 0) {
		await getSearchSuggestion(event.target.value);
	}
};

const setQuery = (title, name) => {
	searchInputVal.value = title;
	showSuggestion.value = false;
	emit('update:searchQuery', title);
};

const handleSearchSubmit = (event) => {
	showSuggestion.value = false;
	if (searchInputVal.value.length < 3) {
		searchInputVal.value = '';
	}
	emit('update:searchQuery', searchInputVal.value);
};

onClickOutside(rootEl, (event) => {
	showSuggestion.value = false;
});

const clearSearchInput = () => {
	searchInputVal.value = '';
	emit('update:searchQuery', searchInputVal.value);
};

watch(props.searchList, () => {
	if (searchInputVal.value) {
		getSearchSuggestion(searchInputVal.value);
	}
});

const { isRTL } = useIsRTL();
</script>



<template>
<div
	ref="rootEl"
	class="Search"
	:class="$attrs.class"
	:style="$attrs.style"
>
	<form class="flex" @submit.prevent="handleSearchSubmit">
		<div class="flex-grow mr-6 relative md:mr-0 h-13.5 z-40 rtl:mr-0 rtl:ml-6 rtl:md:ml-0">
			<input
				ref="searchInputEl"
				v-model="searchInputVal"
				class="input-search"
				type="search"
				:placeholder="props.searchInputPlaceholderText"
				@input="handleOnInput"
				@focus="handleOnFocus"
			/>
			<button
				v-if="(searchInputVal.length > 0)"
				type="button"
				class="clear-icon-wrapper"
				@click="clearSearchInput"
			>
				<icon-fas-circle-xmark class="clear-icon" />
			</button>
			<icon-far-magnifying-glass
				class="fill-primary-blue-base absolute my-auto inset-y-0 left-4 rtl:left-auto rtl:right-4 transform rtl:-rotate-270"
			/>
			<template
				v-if="searchInputVal.length > 2 && searchSuggestionByOrder.length > 0 && showSuggestion"
			>
				<div class="suggestion-list absolute mt-1 w-full">
					<ul class="suggestion-list-wrapper flex flex-col text-neutral-grey-extradark styled-scrollbar">
						<!-- eslint-disable -->
						<li
							v-for="item in searchSuggestionByOrder.slice(0, 5)"
							:key="item.value"
							class="pr-1"
						>
							<button
								type="button"
								class="suggestion-list-item"
								@click="setQuery(item.title, item.name)"
								v-html-sanitize="wrapQueryWithHTML(item.title, { query: searchInputVal, className: 'mark' })"
							>
							</button>
						</li>
						<!-- eslint-enable -->
					</ul>
				</div>
			</template>
		</div>
		<div class="flex-0 max-w-[134px] md:hidden">
			<CTAButton
				:ctaText="props.searchButtonLabelText"
				sizeType="medium"
				:isSubmit="true"
				:disabled="searchInputVal.length < 3"
				class="h-13.5"
			/>
		</div>
	</form>
</div>
</template>



<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.Search {
	height: 54px;

	:deep(.multiselect-no-options) {
		display: none;
	}

	[type="search"]::-webkit-search-cancel-button,
	[type="search"]::-webkit-search-decoration {
		-webkit-appearance: none;
		appearance: none;
	}
}
.input-search {
	width: 100%;
	height: 100%;
	background-color: var(--neutral-grey-ultralight);
	@apply pl-12 pr-14.5;

	[dir="rtl"] & {
		@apply pl-14.5 pr-12;
	}
}
.clear-icon {
	fill: var(--neutral-grey-extradark);
	@apply w-4.5 w-4.5;
}
.clear-icon-wrapper {
	display: flex;
	justify-content: center;
	align-items: center;
	position: absolute;
	@apply w-6 my-auto inset-y-0 p-0 right-5;

	[dir="rtl"] & {
		@apply left-5 right-auto;
	}
}
.suggestion-list {
	top: 54px;
	width: 100%;
	background-color: var(--neutral-white-base);
	border: 2px solid var(--neutral-grey-light);
	border-radius: 12px;
	@apply p-2 pr-1;
}
.suggestion-list-wrapper {
	overflow-y: auto;
	max-height: 258px;
	overscroll-behavior: none;
}
.suggestion-list-item {
	width: 100%;
	text-align: left;
	border-radius: 8px;
	color: var(--neutral-grey-extradark);
	@apply py-2 px-4;

	:deep(.mark) {
		color: var(--text-color);
		background-color: transparent;
	}

	&:hover, &:focus-visible {
		color: var(--primary-blue-base);
		background-color: var(--neutral-grey-extralight);
		text-decoration: none;
		
		:deep(.mark) {
			color: var(--primary-blue-base);
		}
	}
}
</style>
